import type { ReactElement } from 'react'
import { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import Immutable from 'immutable'
import axios from 'axios'
import cc from 'classcat'
import loadable from '@loadable/component'

import { extractVimeoId, extractYoutubeId } from './extractVideoId'
import { useTranslate } from '../../../../../utils/translate'
import PluginPlaceholder from '../components/PluginPlaceholder/PluginPlaceholder'

type VideoData = {
  id: string
  thumbnailUrl: string | Promise<string>
  embedUrl: string
}

const SettingsLayer = loadable(() => import(/* webpackChunkName: "editor" */ '../../SettingsLayer'))
const SettingsForm = loadable(() => import(/* webpackChunkName: "editor" */ './VideoSettings'))

function VideoPreview({ thumbnailUrl }: Readonly<{ thumbnailUrl: string | Promise<string> }>): ReactElement {
  const [url, setUrl] = useState(typeof thumbnailUrl === 'string' ? thumbnailUrl : null)

  useEffect(() => {
    if (thumbnailUrl instanceof Promise) {
      thumbnailUrl.then(setUrl)
    }
  }, [thumbnailUrl])

  return (
    <div className="dali-plugin-video-container">
      <div
        style={{
          backgroundImage: `url("${url}")`,
        }}
        className="dali-plugin-video-thumbnail"
      />
    </div>
  )
}

function VideoEmbedFrame({ url }: Readonly<{ url: string }>): ReactElement {
  return (
    <div className="dali-plugin-video-container">
      {/* eslint-disable-next-line jsx-a11y/iframe-has-title */}
      <iframe
        className="dali-plugin-video-content"
        src={url}
        loading="lazy"
        referrerPolicy="strict-origin-when-cross-origin"
        frameBorder="0"
        allowFullScreen={true}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
      />
    </div>
  )
}

function generateYoutubeEmbedUrl(id: string, origin: string) {
  return `https://www.youtube.com/embed/${id}?rel=0&autohide=1&showinfo=0&wmode=opaque&origin=${encodeURIComponent(origin)}`
}

function generateYoutubeNoCookieEmbedUrl(id: string, origin: string) {
  return `https://www.youtube-nocookie.com/embed/${id}?rel=0&autohide=1&showinfo=0&wmode=opaque&origin=${encodeURIComponent(origin)}`
}

function generateYoutubeThumbnailUrl(id: string) {
  return `https://img.youtube.com/vi/${id}/maxresdefault.jpg`
}

async function generateVimeoThumbnailUrl(id: string): Promise<string> {
  return axios
    .get(`https://vimeo.com/api/v2/video/${id}.json`)
    .then((response) => response.data[0].thumbnail_large)
    .catch(() => '')
}

function generateVimeoEmbedUrl(id: string) {
  return `https://player.vimeo.com/video/${id}?portrait=0`
}

function useVideoData(url: string): VideoData | null {
  const origin = useSelector((state: ImmutableMap) => state.getIn(['shop', 'locationBase', 'origin']))

  const youtubeId = extractYoutubeId(url)
  const vimeoId = extractVimeoId(url)

  if (youtubeId) {
    const isNoCookie = url.includes('youtube-nocookie.com')

    return {
      id: youtubeId,
      thumbnailUrl: generateYoutubeThumbnailUrl(youtubeId),
      // Add origin parameter for iOS/Safari compatibility
      embedUrl: isNoCookie
        ? generateYoutubeNoCookieEmbedUrl(youtubeId, origin)
        : generateYoutubeEmbedUrl(youtubeId, origin),
    }
  } else if (vimeoId) {
    return {
      id: vimeoId,
      thumbnailUrl: generateVimeoThumbnailUrl(vimeoId),
      embedUrl: generateVimeoEmbedUrl(vimeoId),
    }
  }

  return null
}

export default function VideoPlugin({
  data = Immutable.fromJS({ src: '' }),
  editorMode,
  editorView,
  onEdit,
  onCancel,
  onDataChange,
  onSave,
}: Readonly<WorkspacePluginProps>): ReactElement | null {
  const ref = useRef<HTMLDivElement>(null)
  const src = data.get('src')
  const [isActive, setIsActive] = useState(false)

  const t = useTranslate('interface')

  const pluginActiveClasses = cc([
    'dali-plugin-video',
    {
      'dali-grid-element-highlighted': isActive,
    },
  ])

  const video = useVideoData(src)

  const videoPlaceholder = (
    <PluginPlaceholder iconClassName="plugin-placeholder-video" onAdd={() => onEdit()}>
      {t('components.videoElementComponent.addVideoButton.label')}
    </PluginPlaceholder>
  )

  if (editorView) {
    return (
      <div className={pluginActiveClasses} ref={ref}>
        {!video ? videoPlaceholder : <VideoPreview key={video.id} thumbnailUrl={video.thumbnailUrl} />}
        <SettingsLayer
          onActiveStateChange={setIsActive}
          open={editorMode === 'edit'}
          virtualRef={ref}
          side="right"
          onEscapeKeyDown={onCancel}
        >
          {({ renderLayout }) => <SettingsForm {...{ data, onDataChange, onSave, onCancel, renderLayout }} />}
        </SettingsLayer>
      </div>
    )
  }

  return !video ? null : (
    <div className={pluginActiveClasses}>
      <VideoEmbedFrame key={video.id} url={video.embedUrl} />
    </div>
  )
}
